import { useSearchParams, useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { CATEGORIES, CONDITIONS, PRE_LOVED } from "@/data/shop";
import { CATEGORY_SEO } from "@/data/categorySeo";
import { useProducts } from "@/lib/products";
import { ProductCard } from "@/components/shop/ProductCard";
import { Slogan } from "@/components/shop/Slogan";
import { Breadcrumbs } from "@/components/shop/Breadcrumbs";
import { useSeo, categoryPath, breadcrumbLd, absUrl } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

const SORTS = [["featured", "Featured"], ["newest", "Newest first"], ["price-asc", "Price: low to high"], ["price-desc", "Price: high to low"]];
const selectCls = "w-full sm:w-auto h-10 px-3 pr-8 rounded-full bg-white border-2 border-[#202A35]/15 text-sm font-semibold focus:outline-none focus:border-[#E2A100]";
const SHOP_SEO = { title: "Shop All: New, Open-Box & Pre-Loved Bargains", description: "Browse everything at ShopaBee: new, open-box, pre-loved and clearance finds across tech, home, fashion, toys and travel, with clear UK prices." };

const filterProducts = (products, { category, condition, q, max, sort }) => {
  const term = q.toLowerCase();
  const list = products.filter((p) =>
    (!category || (category === "clearance" ? p.clearance : p.categoryId === category)) &&
    (!condition || (condition === "pre-loved" ? PRE_LOVED.includes(p.condition) : p.condition === condition)) &&
    (!max || p.price <= Number(max)) &&
    (!term || `${p.name} ${p.category} ${p.condition} ${p.desc}`.toLowerCase().includes(term))
  );
  if (sort === "price-asc") return [...list].sort((a, b) => a.price - b.price);
  if (sort === "price-desc") return [...list].sort((a, b) => b.price - a.price);
  if (sort === "newest") return [...list].reverse();
  return list;
};

export default function Shop({ onAdd }) {
  const [params, setParams] = useSearchParams();
  const { category: routeCat = "" } = useParams();
  const navigate = useNavigate();
  const legacyCat = params.get("category");
  const f = { category: routeCat, condition: params.get("condition") || "", q: params.get("q") || "", max: params.get("max") || "", sort: params.get("sort") || "featured" };
  const set = (key, value) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next);
  };
  const setCategory = (id) => navigate(`${id ? categoryPath(id) : "/shop"}${params.toString() ? `?${params}` : ""}`);
  const { products } = useProducts();
  const cat = CATEGORIES.find((c) => c.id === f.category);
  const seo = cat ? CATEGORY_SEO[cat.id] : SHOP_SEO;
  const path = cat ? categoryPath(cat.id) : "/shop";
  const crumbs = [{ name: "Home", path: "/" }, { name: "Shop", path: "/shop" }, ...(cat ? [{ name: cat.name, path }] : [])];
  const filtered = !!(f.condition || f.q || f.max || params.get("sort"));
  const unknown = !!routeCat && !cat;
  useSeo(unknown ? { title: "Page not found", noindex: true } : { title: f.q ? `Search results for "${f.q}"` : seo.title, description: seo.description, path, noindex: filtered,
    jsonLd: { "@context": "https://schema.org", "@graph": [breadcrumbLd(crumbs), { "@type": "CollectionPage", name: cat?.name || "Shop All", url: absUrl(path), description: seo.description }] } });

  if (legacyCat) { // old ?category= links forward to the clean address
    const rest = new URLSearchParams(params); rest.delete("category");
    return <Navigate replace to={`${categoryPath(legacyCat)}${rest.toString() ? `?${rest}` : ""}`} />;
  }
  if (unknown) return <NotFound />;
  const results = filterProducts(products, f);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="shop-page">
      <Breadcrumbs items={crumbs} />
      <p className="mt-4 text-xs font-semibold uppercase tracking-[0.2em] mb-2 text-[#202A35]" data-testid="shop-slogan"><Slogan /></p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#202A35]" data-testid="shop-title">{cat?.name || "Shop All"}</h1>
      {cat && <p className="mt-3 max-w-2xl text-sm sm:text-base text-[#202A35]/75 leading-relaxed" data-testid="category-intro">{CATEGORY_SEO[cat.id].intro}</p>}
      {f.q && <p className="mt-2 text-sm text-[#202A35]/70" data-testid="search-summary">Results for “{f.q}” · <button className="underline font-semibold" onClick={() => set("q", "")} data-testid="clear-search-btn">Clear search</button></p>}
      {f.max && <p className="mt-2 text-sm text-[#202A35]/70" data-testid="max-price-summary">Under £{f.max} · <button className="underline font-semibold" onClick={() => set("max", "")} data-testid="clear-max-btn">Show all prices</button></p>}

      <div className="mt-6 grid grid-cols-2 items-center gap-3 sm:flex sm:flex-wrap" data-testid="shop-filters">
        <select aria-label="Category" value={f.category} onChange={(e) => setCategory(e.target.value)} className={selectCls} data-testid="filter-category">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select aria-label="Condition" value={f.condition} onChange={(e) => set("condition", e.target.value)} className={selectCls} data-testid="filter-condition">
          <option value="">Any condition</option>
          <option value="pre-loved">Pre-Loved (all used)</option>
          {Object.keys(CONDITIONS).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select aria-label="Sort" value={f.sort} onChange={(e) => set("sort", e.target.value === "featured" ? "" : e.target.value)} className={selectCls} data-testid="sort-select">
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span className="text-right sm:ml-auto text-sm text-[#202A35]/60" data-testid="results-count">{results.length} {results.length === 1 ? "item" : "items"}</span>
      </div>

      {results.length ? (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="shop-grid">
          {results.map((p) => <ProductCard key={p.id} p={p} onAdd={onAdd} />)}
        </div>
      ) : (
        <div className="mt-10 rounded-2xl bg-[#FFF3C4] p-10 text-center" data-testid="shop-empty">
          <p className="font-display text-xl font-bold text-[#202A35]">Nothing here just yet!</p>
          <p className="mt-1 text-sm text-[#202A35]/70">Try a different filter or search.</p>
          <Link to="/shop" className="inline-block mt-4 h-10 leading-10 px-5 rounded-full bg-[#202A35] text-white text-sm font-bold" data-testid="reset-filters-btn">Reset filters</Link>
        </div>
      )}
      {!cat && !filtered && (
        <nav aria-label="Categories" className="mt-10 flex flex-wrap gap-2" data-testid="shop-category-links">
          {CATEGORIES.map((c) => <Link key={c.id} to={categoryPath(c.id)} className="text-sm font-semibold px-4 py-1.5 rounded-full border-2 border-[#202A35]/15 hover:border-[#202A35]" data-testid={`shop-category-link-${c.id}`}>{c.name}</Link>)}
        </nav>
      )}
      {results.some((p) => p.demo) && <p className="mt-6 text-xs text-[#202A35]/55">Items marked "Demo item" are examples, not real stock.</p>}
    </section>
  );
}
